/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo } from 'react';
import { AppButton } from 'src/components/uikit/buttons/button/button.component';
import { enumToDropdownOptions } from 'src/components/uikit/inputs/dropdown/dropdown-helper';
import {
  AttentionTextVariantEnum,
  ContentLayout,
  ISurveyOption,
  ISurveyQuestion,
  QuestionType,
  SkipOptions,
  SurveyContentBlock,
  SurveyContentImage,
} from 'src/services/api/api-client';
import { QuestionScheme, SurveyOption } from 'src/services/api/api-client';
import { useContextSelector } from 'use-context-selector';
import { changeField } from '../../components/editableField/editableField.component';
import { EditableQuestionContext, QuestionSchemeEditorContext } from '../../container/questionSchemeUIEditor.context';
import { createCommonOption } from '../../helpers/option-helpers';
import { getOptionValueError } from '../../validation/ValidationRules';
import {
  getQuestionPreset,
  getQuestionTypeLabel,
  isFixedOptionCountQuestion,
  normalizeQuestionByType,
} from '../../helpers/questionType-helpers';
import Style from './questionToolBox.module.css';
import { AppTextInput } from 'uikit/inputs';
import { AppInputLabel } from 'uikit/wrappers';
import { AppCheckboxInput } from 'uikit/inputs';
import { AppInputError } from 'uikit/wrappers';
import { AppDropDownWithSuggestionInput } from 'uikit/inputs/dropdown/appDropDownWithSuggestion';
import { AppTextField } from 'uikit/fields';

type EnumDropdownOption = {
  key: number;
  text: string;
};

const isContentQuestion = (questionType: QuestionType) =>
  questionType === QuestionType.Content || questionType === QuestionType.Gallery;

const createEmptyContentBlock = (): SurveyContentBlock =>
  SurveyContentBlock.fromJS({
    titleText: '',
    descriptionText: '',
    image: null,
    layout: ContentLayout.Default,
  });

const updateContentBlock = (
  editableQuestion: ISurveyQuestion,
  index: number,
  updater: (block: SurveyContentBlock) => SurveyContentBlock,
): ISurveyQuestion => {
  const blocks: SurveyContentBlock[] = (editableQuestion.contentBlock ?? []).map((block) =>
    SurveyContentBlock.fromJS(block),
  );
  const current = blocks[index] ?? createEmptyContentBlock();
  blocks[index] = updater(current);
  return { ...editableQuestion, contentBlock: blocks };
};

const cloneContentBlock = (block: SurveyContentBlock, patch: Partial<SurveyContentBlock>): SurveyContentBlock =>
  SurveyContentBlock.fromJS({
    ...block.toJSON(),
    ...patch,
  });

export const QuestionToolBox = () => {
  const questionFromContext = useContextSelector(QuestionSchemeEditorContext, (x) => x.selectedQuestion);
  const setQuestionFromContext = useContextSelector(QuestionSchemeEditorContext, (x) => x.selectQuestion);
  const saveQuestion = useContextSelector(QuestionSchemeEditorContext, (x) => x.saveQuestion);
  const errorsFromContext = useContextSelector(QuestionSchemeEditorContext, (x) => x.errors);
  const serverError = useContextSelector(QuestionSchemeEditorContext, (x) => x.serverError);

  const setEditableQuestion = useContextSelector(EditableQuestionContext, (x) => x.setEditableQuestion)!;
  const editableQuestion = useContextSelector(EditableQuestionContext, (x) => x.editableQuestion);
  const editableOptionId = useContextSelector(EditableQuestionContext, (x) => x.editableOptionId);
  const setEditableOptionId = useContextSelector(EditableQuestionContext, (x) => x.setEditableOptionId)!;
  const deleteOption = useContextSelector(EditableQuestionContext, (x) => x.deleteOption)!;
  const editOption = useContextSelector(EditableQuestionContext, (x) => x.editOption)!;

  useEffect(() => {
    if (!questionFromContext) {
      setEditableQuestion(null);
      return;
    }

    if (editableQuestion?.id !== questionFromContext.id) {
      setEditableQuestion(normalizeQuestionByType(questionFromContext, questionFromContext.questionType));
    }
  }, [editableQuestion?.id, questionFromContext, setEditableQuestion]);

  const saveHandler = useCallback(async () => {
    if (editableQuestion && saveQuestion && questionFromContext) {
      saveQuestion(questionFromContext.id, editableQuestion);
    }
  }, [editableQuestion, questionFromContext, saveQuestion]);

  const cancelHandler = useCallback(() => {
    setEditableQuestion(null);
    return setQuestionFromContext && setQuestionFromContext(null);
  }, [setEditableQuestion, setQuestionFromContext]);

  const onCreateOptionHandler = useCallback(() => {
    if (!editableQuestion) {
      return;
    }

    const preset = getQuestionPreset(editableQuestion.questionType);
    if (!preset.canAddOptions) {
      return;
    }

    const option: ISurveyOption = {
      discriminator: 'CommonOption',
      questionScheme: null,
      optionText: `Option${editableQuestion.options?.length}`,
      optionValue: `Value${editableQuestion.options?.length}`,
      scoreValue: editableQuestion.options?.length ?? 0,
    };

    setEditableQuestion(
      changeField<ISurveyQuestion>(editableQuestion, 'options', [
        ...(editableQuestion.options ?? []),
        createCommonOption(option),
      ]),
    );
  }, [editableQuestion, setEditableQuestion]);

  const addContentBlockHandler = useCallback(() => {
    if (!editableQuestion) {
      return;
    }

    setEditableQuestion({
      ...editableQuestion,
      contentBlock: [
        ...(editableQuestion.contentBlock ?? []).map((block) => SurveyContentBlock.fromJS(block)),
        createEmptyContentBlock(),
      ],
    });
  }, [editableQuestion, setEditableQuestion]);

  const deleteContentBlockHandler = useCallback(
    (blockIndex: number) => {
      if (!editableQuestion) {
        return;
      }

      setEditableQuestion({
        ...editableQuestion,
        contentBlock: (editableQuestion.contentBlock ?? []).filter((_, index) => index !== blockIndex),
      });
    },
    [editableQuestion, setEditableQuestion],
  );

  const moveContentBlockHandler = useCallback(
    (blockIndex: number, direction: 'up' | 'down') => {
      if (!editableQuestion?.contentBlock?.length) {
        return;
      }

      const nextIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
      if (nextIndex < 0 || nextIndex >= editableQuestion.contentBlock.length) {
        return;
      }

      const blocks = editableQuestion.contentBlock.map((block) => SurveyContentBlock.fromJS(block));
      const [block] = blocks.splice(blockIndex, 1);
      blocks.splice(nextIndex, 0, block);
      setEditableQuestion({ ...editableQuestion, contentBlock: blocks });
    },
    [editableQuestion, setEditableQuestion],
  );

  const editContentBlockHandler = useCallback(
    (blockIndex: number, updater: (block: SurveyContentBlock) => SurveyContentBlock) => {
      if (!editableQuestion) {
        return;
      }

      setEditableQuestion(updateContentBlock(editableQuestion, blockIndex, updater));
    },
    [editableQuestion, setEditableQuestion],
  );

  const isErrors = useMemo(() => {
    return Object.keys(errorsFromContext).length > 0;
  }, [errorsFromContext]);

  const errorsText = useMemo(() => {
    return Object.keys(errorsFromContext)
      .map((key) => errorsFromContext[key])
      .join('\n');
  }, [errorsFromContext]);

  const questionTypeOptions = useMemo(
    () =>
      enumToDropdownOptions(QuestionType)
        .filter((x) => x.key !== QuestionType[5])
        .map((option) => ({
          ...option,
          text: getQuestionTypeLabel(QuestionType[option.key as keyof typeof QuestionType] as unknown as QuestionType),
        })) as EnumDropdownOption[],
    [],
  );

  const skipOptions = useMemo(
    () =>
      enumToDropdownOptions(SkipOptions).map((option) => ({
        key: SkipOptions[option.key as keyof typeof SkipOptions],
        text: option.text,
      })),
    [],
  );

  const attentionTextVariantOptions = useMemo(
    () =>
      enumToDropdownOptions(AttentionTextVariantEnum).map((option) => ({
        key: AttentionTextVariantEnum[option.key as keyof typeof AttentionTextVariantEnum],
        text: option.text,
      })),
    [],
  );

  const questionPreset = useMemo(
    () => (editableQuestion ? getQuestionPreset(editableQuestion.questionType) : null),
    [editableQuestion],
  );
  const isDateQuestion =
    editableQuestion?.questionType === QuestionType.DateInput ||
    editableQuestion?.questionType === QuestionType.DateRangeInput;
  const isFreeInputQuestion = editableQuestion?.questionType === QuestionType.FreeInput;
  const isPresentationQuestion = editableQuestion ? isContentQuestion(editableQuestion.questionType) : false;
  const layoutOptions = useMemo(
    () =>
      enumToDropdownOptions(ContentLayout).map((option) => ({
        ...option,
        text: option.text.replace(/([A-Z])/g, ' $1').trim(),
      })),
    [],
  );

  return (
    <div className={Style.container}>
      {editableQuestion ? (
        <>
          <div className={Style.header}>
            <div>
              <div className={Style.title}>{'Properties'}</div>
              <div className={Style.subtitle}>
                {isPresentationQuestion
                  ? 'Configure screen content and layout'
                  : 'Configure question and answer behavior'}
              </div>
            </div>
          </div>
          <div className={Style.content}>
            <div className={Style.panel}>
              <div className={Style.panelTitle}>{'Question'}</div>
              <div className={Style.settings}>
                <AppInputLabel text={'Question text'}>
                  <AppTextInput
                    value={editableQuestion?.questionText}
                    type={'text-area'}
                    className={Style.questionText}
                    onChange={(e) =>
                      setEditableQuestion(
                        changeField<ISurveyQuestion>(editableQuestion, 'questionText', e.target.value),
                      )
                    }
                  />
                </AppInputLabel>
                <AppInputLabel text={'Attention text'}>
                  <AppTextInput
                    value={editableQuestion?.attentionText ?? ''}
                    type={'text-area'}
                    onChange={(e) =>
                      setEditableQuestion(
                        changeField<ISurveyQuestion>(editableQuestion, 'attentionText', e.target.value),
                      )
                    }
                  />
                </AppInputLabel>
                {!!editableQuestion.attentionText?.trim() && (
                  <AppInputLabel text={'Attention text variant'}>
                    <AppDropDownWithSuggestionInput
                      isRequired={true}
                      value={attentionTextVariantOptions.find(
                        (option) => option.key === editableQuestion.attentionTextVariant,
                      )}
                      options={attentionTextVariantOptions}
                      onChange={(option) => {
                        if (option?.key === undefined) {
                          return;
                        }

                        setEditableQuestion(
                          changeField<ISurveyQuestion>(editableQuestion, 'attentionTextVariant', option.key),
                        );
                      }}
                    />
                  </AppInputLabel>
                )}
                <AppInputLabel text="Question type">
                  <AppDropDownWithSuggestionInput
                    value={questionTypeOptions.find(
                      (option) =>
                        editableQuestion.questionType === (QuestionType[option.key] as unknown as QuestionType),
                    )}
                    options={questionTypeOptions}
                    onChange={(option) => {
                      if (option?.key === undefined) {
                        return;
                      }

                      return setEditableQuestion(
                        normalizeQuestionByType(editableQuestion, QuestionType[option.key] as unknown as QuestionType),
                      );
                    }}
                  />
                </AppInputLabel>
              </div>
            </div>

            <div className={Style.panel}>
              <div className={Style.panelTitle}>{'Type'}</div>
              <div className={Style.settings}>
                <AppInputLabel text={'Skip by default'}>
                  <AppDropDownWithSuggestionInput
                    isRequired={true}
                    value={skipOptions.find(
                      (option) => (editableQuestion.isSkippedRender ?? SkipOptions.NotSkip) === option.key,
                    )}
                    options={skipOptions}
                    onChange={(option) => {
                      setEditableQuestion(
                        changeField<ISurveyQuestion>(
                          editableQuestion,
                          'isSkippedRender',
                          option?.key ?? SkipOptions.NotSkip,
                        ),
                      );
                    }}
                  />
                </AppInputLabel>
                <AppCheckboxInput
                  label={'Can be skipped by user'}
                  checked={editableQuestion?.canBeSkipped}
                  onChange={(e) =>
                    setEditableQuestion(
                      changeField<ISurveyQuestion>(editableQuestion, 'canBeSkipped', e.target.checked),
                    )
                  }
                />
                <AppCheckboxInput
                  label={'With previous answer'}
                  checked={!!editableQuestion.withPreviousAnswer}
                  onChange={(e) =>
                    setEditableQuestion(
                      changeField<ISurveyQuestion>(editableQuestion, 'withPreviousAnswer', e.target.checked),
                    )
                  }
                />
                {editableQuestion.questionType === QuestionType.Scale && (
                  <AppCheckboxInput
                    label={'First option is positive'}
                    checked={!!editableQuestion.firsOptionIsPositive}
                    onChange={(e) =>
                      setEditableQuestion(
                        changeField<ISurveyQuestion>(editableQuestion, 'firsOptionIsPositive', e.target.checked),
                      )
                    }
                  />
                )}
              </div>
            </div>

            {isPresentationQuestion ? (
              <div className={Style.panel}>
                <div className={Style.panelHeader}>
                  <div className={Style.panelTitle}>{'Content blocks'}</div>
                  <div className={Style.hint}>
                    {editableQuestion.questionType === QuestionType.Content
                      ? 'Content screen uses contentBlock instead of answer options'
                      : 'Gallery uses ordered content blocks as slides'}
                  </div>
                </div>
                <div className={Style.optionsContainer}>
                  {(editableQuestion.contentBlock ?? []).map((block, index) => (
                    <div key={index} className={Style.optionEditor}>
                      <div className={Style.optionEditorHeader}>
                        <span className={Style.optionIndex}>
                          {editableQuestion.questionType === QuestionType.Gallery ? `Block ${index + 1}` : 'Block'}
                        </span>
                        <div className={Style.contentBlockActions}>
                          {editableQuestion.questionType === QuestionType.Gallery && (
                            <>
                              <AppButton
                                variant={'icon-link'}
                                colorSchema={'secondary'}
                                text="Up"
                                disabled={index === 0}
                                onClick={() => moveContentBlockHandler(index, 'up')}
                              />
                              <AppButton
                                variant={'icon-link'}
                                colorSchema={'secondary'}
                                text="Down"
                                disabled={index === (editableQuestion.contentBlock?.length ?? 0) - 1}
                                onClick={() => moveContentBlockHandler(index, 'down')}
                              />
                            </>
                          )}
                          <AppButton
                            variant={'icon-link'}
                            colorSchema={'decline'}
                            text="Delete"
                            disabled={(editableQuestion.contentBlock?.length ?? 0) <= 1}
                            onClick={() => deleteContentBlockHandler(index)}
                          />
                        </div>
                      </div>

                      <AppTextField
                        value={block.titleText ?? ''}
                        labelProps={{ text: 'Title text' }}
                        onChange={(e) =>
                          editContentBlockHandler(index, (current) =>
                            cloneContentBlock(current, { titleText: e.target.value }),
                          )
                        }
                      />
                      <AppTextField
                        value={block.descriptionText ?? ''}
                        labelProps={{ text: 'Description text' }}
                        type={'text-area'}
                        onChange={(e) =>
                          editContentBlockHandler(index, (current) =>
                            cloneContentBlock(current, { descriptionText: e.target.value }),
                          )
                        }
                      />
                      <AppTextField
                        value={block.image?.resourceKey ?? ''}
                        labelProps={{ text: 'Image resource key' }}
                        onChange={(e) =>
                          editContentBlockHandler(index, (current) =>
                            cloneContentBlock(current, {
                              image: e.target.value.trim().length
                                ? SurveyContentImage.fromJS({
                                    resourceKey: e.target.value,
                                    accessibilityLabel: current.image?.accessibilityLabel ?? null,
                                  })
                                : current.image?.accessibilityLabel
                                ? SurveyContentImage.fromJS({
                                    resourceKey: '',
                                    accessibilityLabel: current.image.accessibilityLabel,
                                  })
                                : null,
                            }),
                          )
                        }
                      />
                      <AppTextField
                        value={block.image?.accessibilityLabel ?? ''}
                        labelProps={{ text: 'Image accessibility label' }}
                        onChange={(e) =>
                          editContentBlockHandler(index, (current) =>
                            cloneContentBlock(current, {
                              image:
                                e.target.value.trim().length || current.image?.resourceKey
                                  ? SurveyContentImage.fromJS({
                                      resourceKey: current.image?.resourceKey ?? '',
                                      accessibilityLabel: e.target.value || null,
                                    })
                                  : null,
                            }),
                          )
                        }
                      />
                      <AppInputLabel text={'Layout'}>
                        <AppDropDownWithSuggestionInput
                          value={layoutOptions.find((option) => block.layout === option.key)}
                          options={layoutOptions}
                          onChange={(option) =>
                            option?.key !== undefined &&
                            editContentBlockHandler(index, (current) =>
                              cloneContentBlock(current, { layout: option.key as ContentLayout }),
                            )
                          }
                        />
                      </AppInputLabel>
                    </div>
                  ))}
                </div>
                {editableQuestion.questionType === QuestionType.Gallery && (
                  <AppButton
                    text={'Add block'}
                    variant={'icon-link'}
                    colorSchema={'primary'}
                    onClick={addContentBlockHandler}
                    className={Style.addOptionButton}
                  />
                )}
              </div>
            ) : (
              <div className={Style.panel}>
                <div className={Style.panelHeader}>
                  <div className={Style.panelTitle}>{'Answer options'}</div>
                  {questionPreset && (
                    <div className={Style.hint}>
                      {isFixedOptionCountQuestion(editableQuestion.questionType)
                        ? `${getQuestionTypeLabel(editableQuestion.questionType)} requires ${
                            questionPreset.optionCount
                          } option${questionPreset.optionCount > 1 ? 's' : ''}`
                        : 'Manage answer options'}
                    </div>
                  )}
                </div>
                <div className={Style.optionsContainer}>
                  {editableQuestion.options?.map((option, i) => (
                    <div
                      key={i}
                      className={clsx(Style.optionEditor, { [Style.selectedOption]: i === editableOptionId })}
                      onClick={() => setEditableOptionId(i)}
                    >
                      <div className={Style.optionEditorHeader}>
                        <span className={Style.optionIndex}>{i + 1}</span>
                        {!isFreeInputQuestion && (
                          <AppButton
                            disabled={!questionPreset?.canDeleteOptions}
                            variant={'icon-link'}
                            colorSchema={'decline'}
                            text="Delete"
                            onClick={() => deleteOption(i)}
                          />
                        )}
                      </div>
                      {isFreeInputQuestion ? (
                        <AppTextField
                          value={option.scoreValue}
                          labelProps={{ text: 'Score value' }}
                          type={'number'}
                          onChange={(e) =>
                            editOption(
                              i,
                              SurveyOption.fromJS({
                                ...option.toJSON(),
                                optionText: '',
                                optionValue: '0',
                                scoreValue: Number(e.target.value),
                              }),
                            )
                          }
                        />
                      ) : (
                        <>
                          <div className={Style.optionGrid}>
                            <AppTextField
                              value={option.optionText}
                              labelProps={{ text: 'Option text' }}
                              onChange={(e) => editOption(i, changeField(option, 'optionText', e.target.value))}
                            />
                            <AppTextField
                              value={option.scoreValue}
                              labelProps={{ text: 'Score value' }}
                              type={'number'}
                              onChange={(e) => editOption(i, changeField(option, 'scoreValue', Number(e.target.value)))}
                            />
                          </div>
                          {!isDateQuestion && (
                            <AppTextField
                              value={option.optionValue}
                              labelProps={{ text: 'Option value' }}
                              errorProps={{ errors: getOptionValueError(editableQuestion, i) }}
                              onChange={(e) => editOption(i, changeField(option, 'optionValue', e.target.value))}
                            />
                          )}
                        </>
                      )}
                      {!isFreeInputQuestion && (
                        <div className={Style.optionChecks}>
                          {!isDateQuestion && (
                            <>
                              <AppCheckboxInput
                                label={'Mark as answered'}
                                checked={option.optionValue === option.answerValue}
                                onChange={(e) =>
                                  editOption(
                                    i,
                                    changeField(
                                      option,
                                      'answerValue',
                                      e.target.checked ? option.optionValue : undefined,
                                    ),
                                  )
                                }
                              />
                              <AppCheckboxInput
                                label={'Mark as default'}
                                checked={option.optionValue === option.defaultValue}
                                onChange={(e) =>
                                  editOption(
                                    i,
                                    changeField(
                                      option,
                                      'defaultValue',
                                      e.target.checked ? option.optionValue : undefined,
                                    ),
                                  )
                                }
                              />
                            </>
                          )}
                          <AppCheckboxInput
                            label={'Conditional option'}
                            checked={!!option.questionScheme}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newOption = new SurveyOption();
                                newOption.init({
                                  ...option,
                                  questionScheme: new QuestionScheme({
                                    version: '',
                                    description: '',
                                    legend: '',
                                    questions: [],
                                  }),
                                });
                                editOption(i, newOption);
                                return;
                              }

                              const newOption = new SurveyOption();
                              newOption.init({ ...option, questionScheme: null });
                              editOption(i, newOption);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {questionPreset?.canAddOptions && !isFreeInputQuestion && (
                  <AppButton
                    text={'Add option'}
                    variant={'icon-link'}
                    colorSchema={'primary'}
                    onClick={onCreateOptionHandler}
                    className={Style.addOptionButton}
                  />
                )}
              </div>
            )}
          </div>

          <div className={Style.footer}>
            <AppInputError errors={[errorsText, serverError].filter(Boolean).join('\n')} hideBorder position={'top'}>
              <div className={Style.buttonContainer}>
                <AppButton
                  text={'Save changes'}
                  variant={'button'}
                  colorSchema={'primary'}
                  onClick={saveHandler}
                  disabled={isErrors}
                />
                <AppButton text={'Cancel'} variant={'button'} colorSchema={'secondary'} onClick={cancelHandler} />
              </div>
            </AppInputError>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
